import { computed } from "vue";
import {
    useDashboardStore,
    useUserStore,
    useProfileStore,
    useEventsStore,
    useApplicationsStore,
    useAssignmentsStore,
    useRatingStore,
    useRecommendationStore
} from "@/stores";
import {storeToRefs} from "pinia";
import {UserRole, UserType} from "@typesDef/user.types";
import {AssignmentStatus} from "@typesDef/assignment.types";
import {ApplicationStatus, ApplicationType} from "@typesDef/application.types";
import {EventApplicationStatus} from "@typesDef/event.types";
import type {ZProfile} from "@schemas/profile.schemas";

export function useAppComposable() {
    const dashboardStore = useDashboardStore();
    const userStore = useUserStore();
    const profileStore = useProfileStore();
    const eventsStore = useEventsStore();
    const applicationStore = useApplicationsStore();
    const assignmentsStore = useAssignmentsStore();
    const ratingStore = useRatingStore();
    const recommendationStore = useRecommendationStore();

    const { latestData } = storeToRefs(dashboardStore); // Use Pinia store or inject the dashboard
    const { currentUser } = storeToRefs(userStore); // Use Pinia store or inject the auth
    const { currentProfile } = storeToRefs(profileStore); // Use Pinia store or inject the profile
    const { currentEvent, myEvents, events } = storeToRefs(eventsStore); // Use Pinia store or inject the
    const { currentApplication } = storeToRefs(applicationStore); // Use Pinia store or inject the application
    const { currentAssignment } = storeToRefs(assignmentsStore); // Use Pinia store or inject the assignments
    const { currentRating } = storeToRefs(ratingStore); // Use Pinia store or inject the rating
    const { currentRecommendation } = storeToRefs(recommendationStore); // Use Pinia store or inject the recommendation


    // Event
    const isEventDraft = computed(() => currentEvent?.value?.isDraft);
    const isEventDeleted = computed(() => !!currentEvent?.value?.deletedAt);
    const isOnboardingEvent = computed(() => currentEvent?.value?.isOnboardingEvent);
    const eventApplicationType = computed(() => currentEvent?.value?.application?.type);
    const eventApplicationStatus = computed(() => currentEvent?.value?.application?.status);
    const eventApplicationDeadline = computed(() => currentEvent?.value?.application?.deadline);

    const isEventApplicationDeadlinePassed = computed(() => {
        if (!eventApplicationDeadline.value) {
            return false;
        }
        const deadline = new Date(eventApplicationDeadline.value);
        return new Date() > deadline;
    });

    const isEventOver = computed(() => {
        const eventEndDateTime = new Date(currentEvent?.value?.endEventDateTime);
        return new Date() > eventEndDateTime;
    });

    const isEventApplicationOpen = computed(() => eventApplicationStatus.value === EventApplicationStatus.open);

    const canApplyToEvent = computed(() => {
        return (
            !isEventApplicationDeadlinePassed.value && isEventApplicationOpen.value ||
                isOnboardingEvent.value
            ) &&
            isUserTypeAnnouncer.value;
    });

    const isEventApplicationClosed = computed(() => eventApplicationStatus.value === EventApplicationStatus.closed);

    // Application
    const applicationStatus = computed(() => currentApplication?.value?.status);
    const isApplicationStatusPending = computed(() => applicationStatus.value === ApplicationStatus.pending);
    const isApplicationStatusAccepted = computed(() => applicationStatus.value === ApplicationStatus.accepted);
    const isApplicationStatusRejected = computed(() => applicationStatus.value === ApplicationStatus.rejected);
    const isApplicationStatusWithdrawn = computed(() => applicationStatus.value === ApplicationStatus.withdrawn);
    const isApplicationStatusCancelled = computed(() => applicationStatus.value === ApplicationStatus.cancelled);

    const applicationType = computed(() => currentApplication?.value?.type);
    const isProviderRequest = computed(() => applicationType.value === ApplicationType.provider_request);
    const isAnnouncerApplication = computed(() => applicationType.value === ApplicationType.announcer_application);
    const applicationTypeToString = computed(() => (isProviderRequest.value ? "Provider Request" : "Event Application"));

    // Assignment
    const assignmentStatus = computed(() => currentAssignment?.value?.status);
    const isAssignmentStatusPending = computed(() => assignmentStatus.value === AssignmentStatus.pending);
    const isAssignmentStatusActive = computed(() => assignmentStatus.value === AssignmentStatus.active);
    const isAssignmentStatusHistory = computed(() => assignmentStatus.value === AssignmentStatus.history);


   // User
    const userRole = computed(() => currentUser?.value?.role);

    const userType = computed(() => currentUser?.value?.type);
    const isValidUser = computed(() => !!currentUser?.value && currentUser?.value?.role !== UserRole.Guest);
    const isUserTypeAnnouncer = computed(() => userType.value === UserType.Announcer);
    const isUserTypeProvider = computed(() => userType.value === UserType.Provider);
    const isUserTypeFan = computed(() => userType.value === UserType.Fan);
    const isUserTypeAdmin = computed(() => userType.value === UserType.Admin);

    // Misc
    const hasToWait = computed(() => {
        return (
            isApplicationStatusPending.value &&
            (
                (isProviderRequest.value && isUserTypeProvider.value) ||
                (isAnnouncerApplication.value && isUserTypeAnnouncer.value)
            )
        );
    });

    const showStatusOperation = computed(() => {
        return (
            isApplicationStatusPending.value &&
            (
                (isProviderRequest.value && isUserTypeAnnouncer.value) ||
                (isAnnouncerApplication.value && isUserTypeProvider.value)
            )
        );
    });

    const isProfileActionable = (profileId: string) => {
        //currentProfile && currentProfile._id !== profile._id
        return currentProfile.value && currentProfile.value._id !== profileId && isValidUser.value;
    }

    const isEventActionable = computed(() => {
        return currentProfile?.value?._id !== currentEvent?.value?.providerId && !isEventDraft.value && isValidUser.value;
    })


    return {
        latestData,
        currentUser,
        currentProfile,
        currentEvent,
        myEvents,
        events,
        currentApplication,
        currentAssignment,
        isEventDeleted,
        isEventDraft,
        isOnboardingEvent,
        eventApplicationType,
        eventApplicationStatus,
        eventApplicationDeadline,
        isEventOver,
        isEventApplicationDeadlinePassed,
        isEventApplicationOpen,
        isEventApplicationClosed,
        canApplyToEvent,
        isApplicationStatusPending,
        isApplicationStatusAccepted,
        isApplicationStatusRejected,
        isApplicationStatusWithdrawn,
        isApplicationStatusCancelled,
        assignmentStatus,
        isAssignmentStatusPending,
        isAssignmentStatusActive,
        isAssignmentStatusHistory,
        isProviderRequest,
        isAnnouncerApplication,
        applicationType,
        applicationTypeToString,
        isValidUser,
        isUserTypeAnnouncer,
        isUserTypeProvider,
        isUserTypeFan,
        isUserTypeAdmin,
        hasToWait,
        showStatusOperation,
        isProfileActionable,
        isEventActionable
    };
}
